import { Discount } from '../../types/DiscountTypes'
import { findBestMatchingComboRule } from './ComboDiscountHelpers'
import { fixMathRounding, getPriceFromCalculation } from './CalculateDiscountHelpers'
import {
  CalculateDiscountLogic,
  Calculation,
  CalculationDiscount,
  CalculationUserInput,
  DiscountType
} from './CalculateDiscountTypes'

/**
 * DiscountComboDiscountLogic
 *
 * Applies a discount when a specific combination of activities is present.
 * Examples:
 *  - [A1, A2] => 50
 *  - [A5, A6] => 100
 *  - [A4, A5, A6] => 200
 *
 * Behaviour:
 *  - A rule matches when ALL courses in that rule are present in the current selection (extras allowed).
 *  - If multiple rules match, we currently pick the one with the highest discount (policy TBD, see TODO below).
 *  - Applies the discount as ONE CalculationDiscount entry on one of the matched activities (the first course in rule).
 */
export class DiscountComboDiscountLogic implements CalculateDiscountLogic {
  type: DiscountType = 'combo'
  discountDefinition: Discount

  constructor(discount: Discount) {
    this.discountDefinition = discount
  }

  private getAllSelectedActivityIds(previousCalculations: Calculation[]): string[] {
    return previousCalculations.map((c) => c.activity.id)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  chooseEligible(previousCalculations: Calculation[], _userInput: CalculationUserInput): string[] {
    // For combo discounts, eligibility is based on the overall selection; return all selected activity IDs.
    return this.getAllSelectedActivityIds(previousCalculations)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  calculate(previousCalculations: Calculation[], _userInput: CalculationUserInput): Calculation[] {
    // Type guard: expect coursesDiscounts array on the discount definition
    const combo = this.discountDefinition as Discount & {
      coursesDiscounts?: { courses: string[]; discount: number }[]
    }

    if (!combo.coursesDiscounts || combo.coursesDiscounts.length === 0) return previousCalculations

    const bestRule = findBestMatchingComboRule(
      combo.coursesDiscounts,
      this.getAllSelectedActivityIds(previousCalculations)
    )
    if (!bestRule) return previousCalculations

    const discountAmount = bestRule.discount

    // Get all activities in the matched rule (maintain order from the rule definition)
    const ruleActivityCalcs = bestRule.courses
      .map((courseId) => previousCalculations.find((c) => c.activity.id === courseId))
      .filter((calc): calc is Calculation => calc !== undefined)

    // Apply discount sequentially: apply as much as possible to each activity until discount is consumed
    let remainingDiscount = discountAmount

    ruleActivityCalcs.forEach((calc) => {
      if (remainingDiscount <= 0) return

      const activityPrice = getPriceFromCalculation(calc)
      // Apply up to the remaining discount, but not more than the activity price
      const activityDiscountAmount = Math.min(remainingDiscount, activityPrice)
      const outputPrice = fixMathRounding(activityPrice - activityDiscountAmount)

      const discountEntry: CalculationDiscount = {
        type: this.type,
        name: this.discountDefinition.name,
        id: this.discountDefinition.id,
        input: activityPrice,
        discount: activityDiscountAmount,
        output: outputPrice,
        discountCalculationType: 'amount',
        discountValue: activityDiscountAmount,
        discountGroup: this.discountDefinition.discountGroup
      }

      calc.discounts = calc.discounts || []
      calc.discounts.push(discountEntry)
      calc.finalPrice = discountEntry.output

      // Reduce remaining discount
      remainingDiscount -= activityDiscountAmount
    })

    return previousCalculations
  }
}
